import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import EditNoteRounded from "@mui/icons-material/EditNoteRounded";
import Header from "../Header";
import ContentAreaWrapper from "../ContentAreaWrapper";
import TabHeader from "../TabHeader";
import BottomButton from "../BottomButton";
import TextField from "../TextField";
import { useCookies } from "react-cookie";
import AIAdmin from "../../api/AIAdmin";
import { FONTS, CARD_STYLES, FORM_LABEL_SX } from "../../constants/styles";

const Reason = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();
    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const [reason, setReason] = useState("");
    const [date, setDate] = useState<string | null>(null);

    const iconBoxSx = {
        width: 32,
        height: 32,
        borderRadius: "10px",
        bgcolor: alpha(primary, 0.1),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
    };

    useEffect(() => {
        const payload = sessionStorage.getItem("freeMyDay");
        if (!payload) return;

        try {
            const parsed = JSON.parse(payload);
            setReason(parsed.reason ?? "");
            setDate(parsed.date ?? null);
        } catch (err) {
            console.error("Invalid freeMyDay payload in sessionStorage", err);
        }
    }, []);

    const handleBack = () => {
        if (date) {
            navigate(`/aiadmin/free-my-day/clients-review?date=${date}`);
        }
    };

    const handleConfirm = async () => {
        const payload = sessionStorage.getItem("freeMyDay");
        if (!payload || !uuid || !brand_id || !location_id) return;

        try {
            const parsed = JSON.parse(payload);
            const fullPayload = { ...parsed, reason };

            const result = await AIAdmin.freeMyDay(
                uuid,
                brand_id,
                location_id,
                fullPayload
            );

            if (!result.ok) {
                throw new Error(result.error);
            }

            sessionStorage.removeItem("freeMyDay");
            navigate("/calendar", { state: { freeMyDaySuccess: true } });
        } catch (err: any) {
            console.error("Failed to process Free My Day:", err);

            const message =
                err?.message || "Something went wrong. Please try again.";

            navigate("/calendar", { state: { error: message } });
        }
    };

    return (
        <>
            <Header />
            <TabHeader name="Optional Reason" onClick={handleBack} />
            <ContentAreaWrapper headerType="tab" noFooter stickyBottomButton>
                <Stack
                    width="100%"
                    flex={1}
                    alignItems="center"
                    spacing={3}
                    pt={2}
                >
                    {/* Icon + heading */}
                    <Stack alignItems="center" spacing={1.5}>
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: "16px",
                                bgcolor: alpha(primary, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <EventBusyRounded
                                sx={{ fontSize: 28, color: primary }}
                            />
                        </Box>
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="18px"
                            fontWeight={700}
                            textAlign="center"
                        >
                            Why are we freeing your day?
                        </Typography>
                        <Typography
                            fontSize="13px"
                            color="text.secondary"
                            textAlign="center"
                            maxWidth="280px"
                        >
                            You can leave it empty. We'll create a polite reason
                            automatically.
                        </Typography>
                    </Stack>

                    {/* Reason card */}
                    <Stack sx={CARD_STYLES} spacing={1.5} width="100%">
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                        >
                            <Box sx={iconBoxSx}>
                                <EditNoteRounded
                                    sx={{ fontSize: 18, color: primary }}
                                />
                            </Box>
                            <Typography sx={FORM_LABEL_SX}>
                                Your explanation (optional)
                            </Typography>
                        </Stack>
                        <TextField
                            multiline
                            fullWidth
                            placeholder="Optional explanation for your clients..."
                            value={reason}
                            minRows={5}
                            onChange={(e) => setReason(e.currentTarget.value)}
                            variant="outlined"
                        />
                    </Stack>
                </Stack>
            </ContentAreaWrapper>
            <BottomButton onClick={handleConfirm} sticky>
                Done
            </BottomButton>
        </>
    );
};

export default Reason;
